import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Dialog } from '../../../../ui';
import { Modal } from '../../../../ui/modal/modal';
import {
  assessmentStages,
  getAssessmentsByUserId,
  getPing,
  navigateNextQuestion,
  startAssessment,
  startPracticeAssessment,
} from '../../../../utils/api_service/assessment_api';
import {
  interviewAnswer,
  interviewAudioAnswer,
  useUploadForm,
} from '../../../../utils/api_service/Behavioral';
import {
  AssessmentQuestionInfo,
  UserAssessment,
  UserAssessmentResponse,
} from '../../../../utils/types/assessment_types';
import { InputDetails } from '../../../../utils/types/utility_types';
import { DIALOG_TYPE } from '../../../job_openings/Constants/DialogTypes';
import { AssessmentComplete } from './assessment_complete/assessment_complete';
import styles from './assigned_assessments.module.css';
import { AssessmentInstructionsModal } from './modal_contents/assessment_instructions_modal/assessment_instructions_modal';
import { AssignedListModal } from './modal_contents/assigned_list_modal/assigned_list_modal';
import { EnableCamMicModal } from './modal_contents/enable_cam_mic_modal/enable_cam_mic_modal';
import { InputSettingsModal } from './modal_contents/input_settings_modal/input_settings_modal';
import { PrivacyNoticeModal } from './modal_contents/privacy_notice_modal/privacy_notice_modal';
import { SelectedAssessmentModal } from './modal_contents/selected_assessment_modal/selected_assessment_modal';
import PreScreenPerform from './PreScreenPerform';
import { Question } from './question/question';

export const IS_IOS = /iPhone|iPod|iPad/.test(navigator.userAgent);

export const AssignedAssessments = () => {
  const [userAssessmentList, setUserAssessmentList] = useState<
    UserAssessment[]
  >([]);
  const [selectedUserAssessment, setSelectedUserAssessment] =
    useState<UserAssessment>();
  const [responseUserAssessment, setResponseUserAssessment] =
    useState<UserAssessmentResponse>();
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>();
  const [videoChunks, setVideoChunks] = useState<Blob[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(true);
  const [isBehaviorModalOpen, setIsBehaviorModalOpen] =
    useState<boolean>(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const [isPractice, setIsPractice] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<number>(0);
  const [afterHelpModalContent, setAfterHelpModalContent] = useState<number>(2);
  const [questionNumber, setQuestionNumber] = useState<number>(0);
  const [questionCount, setQuestionCount] = useState<number>(0);
  const [currentQuestion, setCurrentQuestion] =
    useState<AssessmentQuestionInfo>();
  const [currentAnswer, setCurrentAnswer] = useState<string | File>();

  const [recordedVideo, setRecordedVideo] = useState<string | undefined>();
  const [recordedAudio, setRecordedAudio] = useState<string | undefined>();
  const [assessmentId, setAssessmentId] = useState<string>('');
  const [uploadLink, setUploadLink] = useState<string>('');
  const [assessmentComplete, setAssessmentComplete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [testLoad, setTestLoad] = useState<boolean>(false);
  const [countDown, setCountDown] = useState<number>(1);
  const [runTimer, setRunTimer] = useState<boolean>(false);
  const [remainingTime, setRemainingTime] = useState<number>();
  const [inputDetails, setInputDetails] = useState<InputDetails>({
    cameraId: undefined,
    micId: undefined,
  });
  const videoEl = useRef<HTMLVideoElement>(null);
  const history = useHistory();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [behavioralAudioRecorder, setBehavioralAudioRecorder] =
    useState<MediaRecorder>();
  const [behavioralVideoChunks, setBehavioralVideoChunks] = useState<Blob[]>(
    [],
  );
  const uploadLinkRef = useRef<string>('');
  const previousAttributeRef = useRef<string | undefined>(undefined);
  const [isTimerPaused, setIsTimerPaused] = useState<boolean>(false);
  const [assessmentStarted, setAssessmentStarted] = useState<boolean>(false);
  const [checks, setChecks] = useState<{
    skillAssessment: boolean;
    behaviorAssessment: boolean;
    preScreen: boolean;
  }>({
    skillAssessment: false,
    behaviorAssessment: false,
    preScreen: true,
  });

  const {
    uploadForm,
    uploadQuestionAnswerSkillWithNavigation,
    uploadStatus,
    setUploadStatus,
    progress,
  } = useUploadForm();

  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (runTimer && !isTimerPaused) {
      // For non-behavioral questions, always reset countdown
      if (currentQuestion?.questionType !== 'Behavioral') {
        setCountDown(currentQuestion?.allowedTime || 300);
      }
      // For behavioral questions, countdown is already set in nextQuestion when needed
      // Just start the interval
      timerId = setInterval(() => {
        setCountDown((countDown) => countDown - 1);
      }, 1000);
    }

    return () => clearInterval(timerId);
  }, [
    runTimer,
    isTimerPaused,
    currentQuestion?.allowedTime,
    currentQuestion?.remaining_time,
    currentQuestion?.questionType,
    currentQuestion?.attribute,
  ]);

  useEffect(() => {
    setRemainingTime(countDown);
  }, [countDown, runTimer, currentQuestion?.id, userAssessmentList]);

  useEffect(() => {
    if (assessmentComplete && isPractice) {
      handlePracticeCompletion();
    }
  }, [assessmentComplete, isPractice]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('practice') === 'true') {
      setIsPractice(true);
      setModalContent(4);
    }
  }, []);

  const resetTimer = () => setRunTimer((currentValue) => !currentValue);

  const handleCloseModal = async () => {
    await behavioralInterviewStart(assessmentId);
    setIsBehaviorModalOpen(false);
  };

  const handleCloseModalWarning = async () => {
    functionReturn();
    setIsWarningModalOpen(false);
  };

  // Utility function to stop all media streams
  const stopAllMediaStreams = () => {
    let stoppedCount = 0;

    // Stop and nullify mediaRecorder
    if (mediaRecorder) {
      if (mediaRecorder.stream) {
        mediaRecorder.stream.getTracks().forEach((track) => {
          track.stop();
          stoppedCount++;
        });
      }
      // Try to stop the recorder itself
      try {
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
      } catch (e) {
        console.log('MediaRecorder already inactive');
      }
      setMediaRecorder(undefined);
    }

    // Stop and nullify behavioral audio recorder
    if (behavioralAudioRecorder) {
      if (behavioralAudioRecorder.stream) {
        behavioralAudioRecorder.stream.getTracks().forEach((track) => {
          track.stop();
          stoppedCount++;
        });
      }
      // Try to stop the recorder itself
      try {
        if (behavioralAudioRecorder.state !== 'inactive') {
          behavioralAudioRecorder.stop();
        }
      } catch (e) {
        console.log('BehavioralAudioRecorder already inactive');
      }
      setBehavioralAudioRecorder(undefined);
    }

    // Stop video element stream
    if (videoEl.current && videoEl.current.srcObject) {
      const stream = videoEl.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => {
        track.stop();
        stoppedCount++;
      });
      videoEl.current.srcObject = null;
    }

    // Get all video and audio elements and stop them
    const videos = document.querySelectorAll('video');
    const audios = document.querySelectorAll('audio');

    videos.forEach((video) => {
      if (video.srcObject) {
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          track.stop();
          stoppedCount++;
        });
        video.srcObject = null;
      }
    });

    audios.forEach((audio) => {
      if (audio.srcObject) {
        const stream = audio.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          track.stop();
          stoppedCount++;
        });
        audio.srcObject = null;
      }
    });

    // NUCLEAR OPTION: Query all media devices and check for active tracks
    navigator.mediaDevices.enumerateDevices().then((devices) => {});
  };

  // Stop media streams when assessment is completed
  useEffect(() => {
    if (assessmentComplete) {
      setSelectedUserAssessment(undefined);

      // Clean up the main video element ref immediately
      if (videoEl.current && videoEl.current.srcObject) {
        const stream = videoEl.current.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        videoEl.current.srcObject = null;
      }
      // Stop all other streams
      stopAllMediaStreams();

      // Additional cleanup after a short delay to catch any lingering streams
      const cleanupTimer = setTimeout(() => {
        stopAllMediaStreams();
        // Double-check video ref
        if (videoEl.current && videoEl.current.srcObject) {
          const stream = videoEl.current.srcObject as MediaStream;
          stream.getTracks().forEach((track) => track.stop());
          videoEl.current.srcObject = null;
        }
      }, 500);
      return () => clearTimeout(cleanupTimer);
    }
  }, [assessmentComplete]);

  useEffect(() => {
    stopAllMediaStreams();

    let userId = localStorage.getItem('id');
    if (userId) {
      getAssessmentsByUserId().then((response) => {
        const assessmentsToAdd: UserAssessment[] = [];
        if (response.length > 0) {
          response.forEach((assessment: UserAssessment) => {
            if (assessment.assessmentUser.endTime === undefined) {
              assessmentsToAdd.push(assessment);
            }
          });
        }
        setUserAssessmentList(assessmentsToAdd);
        // Deep-link: an invited candidate can arrive with ?assessment_id=<id>
        // (from their invitation). Pre-select that assessment in the picker so
        // they land on the exact one they were invited to. We do NOT auto-start
        // it — the candidate still confirms and enters the proctored flow.
        const targetId = new URLSearchParams(window.location.search).get(
          'assessment_id',
        );
        if (targetId) {
          const match = assessmentsToAdd.find(
            (a) => a.assessment?.id === targetId,
          );
          if (match) setSelectedUserAssessment(match);
        }
      });
    }

    // Add event listener for browser tab/window close or navigation
    const handleBeforeUnload = () => {
      stopAllMediaStreams();
    };

    // Add event listener for page visibility change (tab switching)
    const handleVisibilityChange = () => {
      if (document.hidden && assessmentComplete) {
        stopAllMediaStreams();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup: Stop all media streams when component unmounts
    return () => {
      stopAllMediaStreams();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [assessmentComplete]);

  useEffect(() => {
    if (selectedUserAssessment?.assessment.id) {
      setAssessmentId(selectedUserAssessment?.assessment.id);
      assessmentTypes(selectedUserAssessment.assessmentUser);
    }

    const assessmentPing = async () => {
      try {
        if (selectedUserAssessment) {
          await getPing(selectedUserAssessment?.assessmentUser.id);
        }
      } catch (e) {
        console.log(e);
      }
    };

    if (assessmentStarted) {
      assessmentPing();
    }

    const intervalId = setInterval(assessmentPing, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [selectedUserAssessment, assessmentStarted]);

  // Clean up media streams when returning to welcome screen (modal content 0)
  useEffect(() => {
    if (modalContent === 0 && isModalOpen) {
      stopAllMediaStreams();

      // Additional aggressive cleanup with delay
      setTimeout(() => {
        stopAllMediaStreams();

        // Force reload all video elements to release hardware
        const videos = document.querySelectorAll('video');
        videos.forEach((video) => {
          video.srcObject = null;
          video.load(); // Force reload to release hardware
        });
      }, 500);
    }
  }, [modalContent, isModalOpen]);

  useEffect(() => {
    if (!videoEl) {
      return;
    }

    if (!isModalOpen) {
      navigator.mediaDevices
        .getUserMedia({
          audio: {
            deviceId: inputDetails.micId,
          },
          video: {
            deviceId: inputDetails.cameraId,
          },
        })
        .then((mediaStream) => {
          // Regular video recorder
          setMediaRecorder(new MediaRecorder(mediaStream));

          // Audio-only recorder for behavioral questions
          const audioStream = new MediaStream(mediaStream.getAudioTracks());
          setBehavioralAudioRecorder(
            new MediaRecorder(audioStream, {
              mimeType: 'audio/webm', // Explicitly set audio mime type
            }),
          );

          let video = videoEl.current;
          if (video) {
            video.muted = true;
            video.srcObject = mediaStream;
            video.autoplay = true;
          }
        });
    }
  }, [videoEl, isModalOpen]);

  useEffect(() => {
    if (mediaRecorder) {
      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          if (currentQuestion?.questionType === 'Behavioral') {
            // Store in separate state for behavioral questions
            setBehavioralVideoChunks((chunks) => [...chunks, event.data]);
          } else {
            // Store in regular videoChunks for other question types
            setVideoChunks((videoChunks) => [...videoChunks, event.data]);
          }
        }
      };
    }
  }, [mediaRecorder, currentQuestion?.questionType]);

  useEffect(() => {
    if (behavioralAudioRecorder) {
      behavioralAudioRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current = [...audioChunksRef.current, event.data];
        }
      };
    }
  }, [behavioralAudioRecorder]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (currentQuestion && !isModalOpen) {
      // Small delay to ensure recorders are fully initialized
      const timer = setTimeout(() => {
        startRecording();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [currentQuestion, isModalOpen]);

  const assessmentTypes = async (assessmentUser: UserAssessmentResponse) => {
    try {
      const stages = await assessmentStages(assessmentUser.assessmentId);

      if (stages.ok) {
        const dataStages = await stages.json();
        setChecks(dataStages);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleGoFullScreen = () => {
    if (elementRef.current) {
      if (elementRef.current.requestFullscreen) {
        elementRef.current.requestFullscreen();
      }
    }
  };

  const handleExitFullScreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  const handleModalToggle = () => {
    stopAllMediaStreams();
    setIsModalOpen(false);
    history.push('/home');
  };

  const startRecording = () => {
    if (currentQuestion?.questionType === 'Behavioral') {
      // Record BOTH audio and video for behavioral questions
      audioChunksRef.current = [];
      setBehavioralVideoChunks([]); // Clear previous behavioral video chunks

      if (
        behavioralAudioRecorder &&
        behavioralAudioRecorder.state === 'inactive'
      ) {
        behavioralAudioRecorder.start(10);
      }

      // Also start video recording for behavioral questions
      if (mediaRecorder && mediaRecorder.state === 'inactive') {
        mediaRecorder.start(10);
      }
    } else if (mediaRecorder) {
      setVideoChunks([]);
      if (mediaRecorder.state === 'inactive') {
        mediaRecorder.start(10);
      }
    }
  };

  const stopRecording = () => {
    if (currentQuestion?.questionType === 'Behavioral') {
      if (
        behavioralAudioRecorder &&
        behavioralAudioRecorder.state === 'recording'
      ) {
        behavioralAudioRecorder.stop();
      }
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
    } else if (mediaRecorder) {
      if (mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
    }
  };

  const initiateAssessment = async (
    assessmentUser: UserAssessmentResponse,
    checks: {
      skillAssessment: boolean;
      behaviorAssessment: boolean;
      preScreen: boolean;
    },
  ) => {
    try {
      if (checks.skillAssessment) {
        if (assessmentUser.startTime === undefined) {
          try {
            const response = await startAssessment(assessmentUser.id);
            if (response.ok) {
              const data = await response.json();
              setQuestionCount(data.questionCount);
              setCurrentQuestion(data);
              setQuestionNumber(questionNumber + 1);
              setLoading(false);
              resetTimer();
            } else {
              setLoading(false);
              if (checks.behaviorAssessment) {
                setIsBehaviorModalOpen(true);
              } else {
                setAssessmentComplete(true);
              }
            }
          } catch (e) {
            setLoading(false);
            if (checks.behaviorAssessment) {
              setIsBehaviorModalOpen(true);
            } else {
              setAssessmentComplete(true);
            }
          }
        } else {
          navigateNextQuestion(assessmentUser.id).then((response) => {
            setLoading(false);
            if (response.id !== 0) {
              setCurrentQuestion(response);
              setQuestionNumber(questionNumber + 1);
              resetTimer();
            } else {
              if (checks.behaviorAssessment) {
                setIsBehaviorModalOpen(true);
              } else {
                setAssessmentComplete(true);
                handleExitFullScreen();
              }
            }
          });
        }
      } else if (checks.behaviorAssessment) {
        await behavioralInterviewStart(assessmentUser.assessmentId);
        setLoading(false);
      } else {
        setLoading(false);
        setAssessmentComplete(true);
      }
    } catch (error) {
      console.log('error');
    }
  };

  const finishedPreScreen = (userAssessment: UserAssessment) => {
    setSelectedUserAssessment(userAssessment);
    commenceAssessment();
  };

  const functionReturn = () => {
    setAssessmentStarted(true);
    if (checks.preScreen && !isPractice) {
      setIsModalOpen(false);
    } else {
      commenceAssessment();
    }
  };

  const commenceAssessment = () => {
    setIsModalOpen(false);
    setLoading(true);
    handleGoFullScreen();
    if (isPractice) {
      startPracticeAssessment().then((response) => {
        setResponseUserAssessment(response);

        // Derive checks from response
        const newChecks = {
          skillAssessment: response.skillStatus === 'assigned',
          behaviorAssessment: response.behaviorStatus === 'assigned',
          preScreen:
            response.assessment.preScreen &&
            response.assessment.preScreen.length > 0,
        };
        setChecks(newChecks);

        // If pre-screen exists and not completed, flow might differ,
        // but user wanted preScreen skipped/handled.
        // Assuming response.preScreenStatus check or just trusting the 'checks' logic.

        if (newChecks.preScreen && response.preScreenStatus !== 'completed') {
          const practiceAssessment = {
            assessmentUser: response,
            assessment: response.assessment,
            assessmentSettings: {} as any,
          };
          setSelectedUserAssessment(practiceAssessment);
          // For practice, we can still call initiateAssessment to get the first question
          initiateAssessment(response, newChecks);
        } else {
          initiateAssessment(response, newChecks);
        }
      });
    } else if (selectedUserAssessment) {
      setIsPractice(false); // Force false if starting real one
      setResponseUserAssessment(selectedUserAssessment.assessmentUser);
      initiateAssessment(selectedUserAssessment.assessmentUser, checks);
    }
  };

  const handleNavigationResult = (navResponse: any) => {
    setIsTimerPaused(false);
    setCurrentAnswer(undefined);
    setRecordedVideo(undefined);
    setLoading(false);

    if (navResponse.id !== 0) {
      setCurrentQuestion(navResponse);
      setQuestionNumber(questionNumber + 1);
      resetTimer();
      setTestLoad(false);
    } else {
      setTestLoad(false);
      if (navResponse.behaviorAssessment) {
        setIsBehaviorModalOpen(true);
      } else if (!navResponse.behaviorAssessment) {
        setAssessmentComplete(true);
      }
      handleExitFullScreen();
    }
  };

  const handleQuestionSubmission = async (
    video: File,
    timeExpired: boolean,
  ) => {
    if (!responseUserAssessment) return;

    if (typeof currentAnswer === 'string' && currentQuestion) {
      resetTimer();
      try {
        setIsTimerPaused(true);
        // Use the combined function
        const result = await uploadQuestionAnswerSkillWithNavigation(
          currentQuestion.id,
          timeExpired.toString(),
          responseUserAssessment.id, // userAssessmentId for navigation
          currentAnswer,
          video,
        );

        setQuestionCount(questionCount - 1);

        // Process navigation result directly
        handleNavigationResult(result.navigationData);
      } catch (e) {
        console.log(e);
      }
    } else if (currentAnswer instanceof Blob && currentQuestion) {
      resetTimer();
      try {
        const result = await uploadQuestionAnswerSkillWithNavigation(
          currentQuestion.id,
          timeExpired.toString(),
          responseUserAssessment.id,
          'Sample',
          currentAnswer,
        );

        setQuestionCount(questionCount - 1);
        handleNavigationResult(result.navigationData);
      } catch (e) {
        console.log(e);
      }
    } else if (!currentAnswer && currentQuestion) {
      resetTimer();
      try {
        const result = await uploadQuestionAnswerSkillWithNavigation(
          currentQuestion.id,
          timeExpired.toString(),
          responseUserAssessment.id,
          '',
          video,
        );

        setQuestionCount(questionCount - 1);
        handleNavigationResult(result.navigationData);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const submitNoFile = async (timeExpired: boolean) => {
    if (currentQuestion?.questionType === 'Classic' || currentQuestion?.questionType === 'Modern') {
      if (!responseUserAssessment) return;
      resetTimer();
      try {
        const result = await uploadQuestionAnswerSkillWithNavigation(
          currentQuestion.id,
          timeExpired.toString(),
          responseUserAssessment.id,
          '',
        );
        setQuestionCount(questionCount - 1);
        handleNavigationResult(result.navigationData);
      } catch (e) {
        console.error(e);
      }
    } else if (currentQuestion?.questionType === 'Behavioral') {
      if (currentQuestion.user_question_assessment_id) {
        setUploadStatus('loading');
        try {
          const response = await interviewAudioAnswer(
            currentQuestion.user_question_assessment_id,
            currentQuestion.id,
            '',
          );

          if (response.ok) {
            const res = await response.json();
            setUploadStatus('initial');
            setCurrentAnswer(undefined);
            setRecordedVideo(undefined);
            setRecordedAudio(undefined);
            setLoading(false);

            if (res.completed) {
              setAssessmentComplete(true);
            } else {
              const { question_id, ...rest } = res;
              const transformed = {
                ...rest,
                id: question_id,
                questionType: 'Behavioral',
              };
              setCurrentQuestion(transformed);

              // ✅ Store NEXT question's upload link in both state and ref
              setUploadLink(res.upload_link);
              uploadLinkRef.current = res.upload_link;

              const hasAttributeChanged =
                previousAttributeRef.current !== undefined &&
                previousAttributeRef.current !== transformed.attribute;

              // Update the ref with new attribute
              previousAttributeRef.current = transformed.attribute;

              if (hasAttributeChanged) {
                setQuestionNumber(questionNumber + 1);
                setQuestionCount(questionCount - 1);
              }

              setIsTimerPaused(false);
              setCountDown(transformed.remaining_time || 300);

              // Reset timer to restart the interval
              // If runTimer is currently true, this will set it to false then back to true
              // If runTimer is currently false, this will set it to true
              if (!runTimer) {
                resetTimer(); // Start the timer if it's not running
              } else {
                // Force a restart by toggling twice
                setRunTimer(false);
                setTimeout(() => setRunTimer(true), 0);
              }
            }
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  };

  const nextQuestion = async (
    sampleBlob?: Blob[],
    sampleAudioBlob?: Blob[],
    skip: boolean = false,
  ) => {
    stopRecording();

    if (currentQuestion?.questionType !== 'Behavioral') {
      setTestLoad(true);

      const videoFile = new File(
        sampleBlob?.length ? sampleBlob : videoChunks,
        'sample.webm',
        { type: 'video/webm' },
      );

      handleQuestionSubmission(videoFile, false);
    } else {
      const audioBlob = new Blob(sampleAudioBlob, {
        type: 'audio/webm;codecs=opus',
      });
      const audioURL = URL.createObjectURL(audioBlob);

      if (currentQuestion.user_question_assessment_id) {
        try {
          setLoading(true);
          setIsTimerPaused(true);

          const videoFile = new File(
            sampleBlob?.length ? sampleBlob : behavioralVideoChunks,
            'sample.webm',
            { type: 'video/webm' },
          );

          const audioFile = new File(
            sampleAudioBlob?.length ? sampleAudioBlob : behavioralVideoChunks,
            'audio.webm',
            { type: 'audio/webm' },
          );

          // ✅ Capture chunks and use REF for upload link (immediate, not async state)
          const videoChunksToUpload: Blob[] =
            sampleAudioBlob && sampleAudioBlob.length > 0
              ? sampleAudioBlob
              : behavioralVideoChunks;
          const currentUploadLink = uploadLinkRef.current; // Use ref instead of state

          // Clear immediately
          setBehavioralVideoChunks([]);
          audioChunksRef.current = [];

          const videoBlob = new Blob(videoChunksToUpload, {
            type: 'video/webm',
          });

          // Upload video to AWS for CURRENT question
          if (
            currentUploadLink &&
            currentUploadLink !== '' &&
            videoBlob.size > 0
          ) {
            const response = await uploadForm(
              currentQuestion.user_question_assessment_id,
              currentQuestion.id,
              '',
              audioFile,
              currentUploadLink, // Pass AWS upload link
              videoFile, // Pass video blob
            );

            setCurrentAnswer(undefined);
            setRecordedVideo(undefined);
            setRecordedAudio(undefined);
            setLoading(false);

            if (response.completed) {
              setAssessmentComplete(true);
            } else {
              const { question_id, ...rest } = response;
              const transformed = {
                ...rest,
                id: question_id,
                questionType: 'Behavioral',
              };
              setCurrentQuestion(transformed);

              // ✅ Store NEXT question's upload link in both state and ref
              setUploadLink(response.upload_link);
              uploadLinkRef.current = response.upload_link;

              const hasAttributeChanged =
                previousAttributeRef.current !== undefined &&
                previousAttributeRef.current !== transformed.attribute;

              // Update the ref with new attribute
              previousAttributeRef.current = transformed.attribute;

              if (hasAttributeChanged) {
                setQuestionNumber(questionNumber + 1);
                setQuestionCount(questionCount - 1);
              }

              setIsTimerPaused(false);
              setCountDown(transformed.remaining_time || 300);

              // Reset timer to restart the interval
              // If runTimer is currently true, this will set it to false then back to true
              // If runTimer is currently false, this will set it to true
              if (!runTimer) {
                resetTimer(); // Start the timer if it's not running
              } else {
                // Force a restart by toggling twice
                setRunTimer(false);
                setTimeout(() => setRunTimer(true), 0);
              }
            }
          }
        } catch (e) {
          setLoading(false);
        }
      }
    }
  };

  const behavioralInterviewStart = async (assessmentId: string) => {
    try {
      const response = await interviewAnswer(assessmentId);
      if (!response.ok) return setAssessmentComplete(true);

      const res = await response.json();
      setQuestionCount(res.question_count);
      const { question_id, ...rest } = res;

      // ✅ Set ref FIRST
      if (res.upload_link) {
        uploadLinkRef.current = res.upload_link;
        setUploadLink(res.upload_link);
      } else {
        console.warn(
          '⚠️ No upload_link received from API for first behavioral question',
        );
      }

      // ✅ THEN set question
      const transformed = {
        ...rest,
        id: question_id,
        questionType: 'Behavioral',
      };

      previousAttributeRef.current = transformed.attribute;

      setCurrentQuestion(transformed);
      setQuestionNumber(1);
      setCountDown(transformed.remaining_time);
      resetTimer();
    } catch (e) {
      setAssessmentComplete(true);
    }
  };

  const handlePracticeCompletion = () => {
    // Stop all media streams (camera and microphone)
    stopAllMediaStreams();

    // Additional aggressive cleanup after a delay
    setTimeout(() => {
      stopAllMediaStreams();

      // Force reload video elements to release hardware
      const videos = document.querySelectorAll('video');
      videos.forEach((video) => {
        video.srcObject = null;
        video.load(); // Force reload to release hardware
      });
    });

    setQuestionNumber(0);
    setQuestionCount(0);
    setCurrentQuestion(undefined);
    setIsPractice(false);
    setIsModalOpen(true);
    setModalContent(0);
    setAssessmentComplete(false);
    handleExitFullScreen();
  };

  const loadModalContent = () => {
    if (modalContent === 0) {
      return (
        <AssignedListModal
            userAssessmentList={userAssessmentList}
            selectedUserAssessment={selectedUserAssessment}
            setSelectedUserAssessment={setSelectedUserAssessment}
            setModalContent={setModalContent}
            setIsPractice={setIsPractice}
            setAfterHelpModalContent={setAfterHelpModalContent}
        />
      );
    } else if (modalContent === 5) {
      return (
        <PrivacyNoticeModal
            setModalContent={setModalContent}
            isPractice={isPractice}
        />
      );
    } else if (modalContent === 1) {
      return (
        <SelectedAssessmentModal
            selectedAssessment={selectedUserAssessment}
            setModalContent={setModalContent}
            setIsPractice={setIsPractice}
            setAfterHelpModalContent={setAfterHelpModalContent}
        />
      );
    } else if (modalContent === 2) {
      return <EnableCamMicModal setModalContent={setModalContent} />;
    } else if (modalContent === 4) {
      return (
        <AssessmentInstructionsModal
            setModalContent={setModalContent}
            setIsPractice={setIsPractice}
            userAssessmentList={userAssessmentList}
            afterHelpModalContent={afterHelpModalContent}
        />
      );
    } else {
      return (
        <InputSettingsModal
            commenceAssessment={functionReturn}
            setIsWarningModalOpen={setIsWarningModalOpen}
            inputDetails={inputDetails}
            setInputDetails={setInputDetails}
            setIsModalOpen={setIsModalOpen}
        />
      );
    }
  };

  const loadModalHeaderText = () => {
    if (modalContent === 1 && selectedUserAssessment) {
      return selectedUserAssessment.assessment.assessmentTitle;
    } else if (modalContent === 2) {
      return 'Allow microphone & camera access';
    } else if (modalContent === 3) {
      return 'System setup';
    } else if (modalContent === 4) {
      return 'First Time User Instructions';
    } else {
      return '';
    }
  };

  const renderQuestion = () => {
    if (checks.preScreen && !isPractice) {
      return (
        <PreScreenPerform
            preScreenQuestions={
              selectedUserAssessment?.assessment?.preScreen
                ? selectedUserAssessment?.assessment.preScreen
                : []
            }
            selectedUserAssessment={selectedUserAssessment}
            finishedPreScreen={finishedPreScreen}
        />
      );
    }

    if (!assessmentComplete) {
      return (
        <Question
            questionNumber={questionNumber}
            questionCount={questionCount}
            selectedAssessment={selectedUserAssessment}
            videoElementRef={videoEl}
            currentQuestion={currentQuestion}
            currentAnswer={currentAnswer}
            setCurrentAnswer={setCurrentAnswer}
            nextQuestion={nextQuestion}
            recordedVideo={recordedVideo}
            setRecordedVideo={setRecordedVideo}
            timeRemaining={remainingTime}
            isPractice={isPractice}
            recordedAudio={recordedAudio}
            setRecordedAudio={setRecordedAudio}
            loading={loading}
            progress={progress}
            uploadStatus={uploadStatus}
            setUploadStatus={setUploadStatus}
            handleQuestionSubmission={handleQuestionSubmission}
            responseUserAssessment={responseUserAssessment}
            handleNavigationResult={handleNavigationResult}
            setQuestionCount={setQuestionCount}
            submitNoFile={submitNoFile}
        />
      );
    } else if (assessmentComplete && isPractice) {
      // Logic moved to useEffect
      return null;
    } else {
      return <AssessmentComplete />;
    }
  };

  const handleWarningApply = () => {
    setIsWarningModalOpen(false);
    functionReturn();
  };

  return (
    <div className='w-full h-screen overflow-hidden bg-gray-50'>
      <div
          className={`h-full w-full overflow-hidden ${
            isFullScreen &&
          'flex justify-center items-center bg-white px-2 md:px-20'
          }`}
          ref={elementRef}
      >
        { renderQuestion() }
      </div>

      <Dialog
          isOpen={isBehaviorModalOpen}
          onClose={handleCloseModal}
          title='Behaviourial Assessment Notice'
          dialogType={DIALOG_TYPE.INFO}
          justify='center'
          confirmButtonLable='Continue'
          cancelButtonLable='Cancel'
          handleApply={async () => {
            try {
              await behavioralInterviewStart(assessmentId);
              setIsBehaviorModalOpen(false); // close AFTER success
            } catch (error) {
              console.error(error);
            // optionally show a toast or error message
            }
          }}
      >
        <>
          <p>
            You will now be asked a number of Behavioural Questions to describe
            real examples from your past experience that will demonstrate how
            you typically handle situations and perform at work.
          </p>

          <p className='mt-2 text-sm text-gray-600'>
            <strong>Note:</strong> The number above is the number of Main
            Behaviour questions that are remaining. Each Main question may
            consist of up to 3 follow up questions.
          </p>
        </>
      </Dialog>
      <Dialog
          isOpen={isWarningModalOpen}
          onClose={handleCloseModalWarning}
          title='WARNING'
          dialogType={DIALOG_TYPE.ERROR}
          justify='center'
          confirmButtonLable='Continue'
          handleApply={handleWarningApply}
      >
        <>
          <p>
            This system can detect the use of Generative AI platforms in spoken
            answers.
          </p>

          <p className='mt-2 text-sm text-gray-600'>
            If you use Generative AI to provide answers then you may be
            blacklisted from this Job Opening and future advertised positions.
          </p>
        </>
      </Dialog>
      <Modal
          visible={isModalOpen}
          toggle={handleModalToggle}
          modalContent={loadModalContent()}
          customClass={styles.modalCustomClass}
          headerClass={classNames(styles.headerBackground, {
            [styles.headerBackgroundImage]: ![2, 4].includes(modalContent),
          })}
          headerText={loadModalHeaderText()}
      />
      { /*<Loader loading={loading}/>*/ }
    </div>
  );
};
